import { BaseService } from "./baseService";
import {
  Project,
  ProjectListResponse,
  ProjectMember,
  ProjectTask,
  ProjectStreamItem,
  ListResponse,
  Attachment,
  ProjectBoard,
  Account,
  ProjectPlanResponse,
  ProjectTaskDetail,
  ProjectGroup,
} from "../types";
import apiClient from "./api";
import {
  generateCacheKey,
  staleWhileRevalidate,
} from "../storage/cacheManager";
import {
  requestDeduplication,
  generateRequestKey,
} from "../utils/requestDeduplication";

class ProjectService extends BaseService<Project> {
  constructor() {
    super("Project");
    this.setCacheStrategy("stale-while-revalidate");
    this.setCacheExpiry(30 * 60 * 1000); // 30 минут
  }

  async getProjects(params?: {
    maxSize?: number;
    offset?: number;
    orderBy?: string;
    order?: "asc" | "desc";
    attributeSelect?: string[];
  }): Promise<ProjectListResponse> {
    const {
      maxSize = 50,
      offset = 0,
      orderBy = "createdAt",
      order = "desc",
      attributeSelect = [
        "cProgres",
        "dateEnd",
        "dateStart",
        "activeGroupId",
        "activeGroupName",
        "accountId",
        "accountName",
        "status",
        "name",
      ],
    } = params || {};

    const queryParams = new URLSearchParams({
      maxSize: maxSize.toString(),
      offset: offset.toString(),
      orderBy,
      order,
      attributeSelect: attributeSelect.join(","),
    });

    const url = `/Project?${queryParams.toString()}`;
    const cacheKey = "projects:list";
    const requestKey = generateRequestKey("GET", url, params);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ProjectListResponse> => {
      const response = await apiClient.get<ProjectListResponse>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectById(projectId: string): Promise<Project> {
    const url = `/Project/${projectId}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (signal?: AbortSignal): Promise<Project> => {
      const response = await apiClient.get<Project>(url, { signal });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectMembers(
    projectId: string,
  ): Promise<ListResponse<ProjectMember>> {
    const queryParams = new URLSearchParams({
      maxSize: "15",
      offset: "0",
      orderBy: "projectRole",
      order: "asc",
      attributeSelect:
        "projectRole,emailAddressIsOptedOut,emailAddressIsInvalid,emailAddress,emailAddressData,salutationName,firstName,lastName,middleName,name",
    });
    const url = `/Project/${projectId}/members?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<ProjectMember>> => {
      const response = await apiClient.get<ListResponse<ProjectMember>>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectTasks(projectId: string): Promise<ListResponse<ProjectTask>> {
    const queryParams = new URLSearchParams({
      maxSize: "15",
      offset: "0",
      orderBy: "createdAt",
      order: "desc",
      attributeSelect:
        "timeline,assignedUserId,assignedUserName,columnId,columnName,name",
    });
    const url = `/Project/${projectId}/tasks?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<ProjectTask>> => {
      const response = await apiClient.get<ListResponse<ProjectTask>>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectStream(
    projectId: string,
  ): Promise<ListResponse<ProjectStreamItem>> {
    const queryParams = new URLSearchParams({
      maxSize: "15",
      offset: "0",
      orderBy: "number",
      order: "desc",
    });
    const url = `/Project/${projectId}/stream?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<ProjectStreamItem>> => {
      const response = await apiClient.get<ListResponse<ProjectStreamItem>>(
        url,
        { signal },
      );
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectAttachments(projectId: string): Promise<Attachment[]> {
    if (!projectId) return [];

    const project = await this.getProjectById(projectId);
    if (!project.cAttachmentIds || project.cAttachmentIds.length === 0) {
      return [];
    }

    return project.cAttachmentIds.map((id) => ({
      id,
      name: project.cAttachmentNames[id] || "Unknown",
      url: `/Attachment/${id}`,
      type: project.cAttachmentTypes[id] || "application/octet-stream",
      parentType: "Project",
      createdById: project.createdById,
    }));
  }

  async createProject(data: Partial<Project>): Promise<Project> {
    const result = await this.create(data);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("projects");
    return result;
  }

  async updateProject(
    projectId: string,
    data: Partial<Project>,
  ): Promise<Project> {
    const result = await this.update(projectId, data);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("projects");
    return result;
  }

  async deleteProject(projectId: string): Promise<void> {
    await this.delete(projectId);
    await this.invalidateCache(projectId);
    const { invalidateListCache } = await import("../storage/cacheManager");
    await invalidateListCache("projects");
    const { removeFromCache, generateCacheKey } =
      await import("../storage/cacheManager");
    const relatedCacheKeys = [
      generateCacheKey(`/Project/${projectId}/members`, {}),
      generateCacheKey(`/Project/${projectId}/tasks`, {}),
      generateCacheKey(`/Project/${projectId}/attachments`, {}),
    ];
    await Promise.all(relatedCacheKeys.map((key) => removeFromCache(key)));
  }

  async getProjectPlan(
    projectId: string,
    params?: {
      maxSize?: number;
      select?: string;
      primaryFilter?: string;
      collapsedGroupIds?: string;
    },
  ): Promise<ProjectPlanResponse> {
    const {
      maxSize = 50,
      select = "timeline,assignedUserId,assignedUserName,columnId,columnName,name,boardId",
      primaryFilter = "planOrCompletedParent",
      collapsedGroupIds,
    } = params || {};

    const queryParams = new URLSearchParams({
      maxSize: maxSize.toString(),
      select,
      primaryFilter,
    });

    if (collapsedGroupIds) {
      queryParams.append("collapsedGroupIds", collapsedGroupIds);
    }

    const url = `/Project/${projectId}/plan?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ProjectPlanResponse> => {
      const response = await apiClient.get<ProjectPlanResponse>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getGroupTasks(
    groupId: string,
    params?: {
      maxSize?: number;
      offset?: number;
      orderBy?: string;
      order?: "asc" | "desc";
      primaryFilter?: string;
      attributeSelect?: string;
    },
  ): Promise<ListResponse<ProjectTask>> {
    const {
      maxSize = 50,
      offset = 0,
      orderBy = "order",
      order = "asc",
      primaryFilter = "planOrCompletedParent",
      attributeSelect = "timeline,assignedUserId,assignedUserName,columnId,columnName,name,boardId",
    } = params || {};

    const queryParams = new URLSearchParams({
      maxSize: maxSize.toString(),
      offset: offset.toString(),
      orderBy,
      order,
      primaryFilter,
      attributeSelect,
    });

    const url = `/ProjectGroup/${groupId}/tasks?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<ProjectTask>> => {
      const response = await apiClient.get<ListResponse<ProjectTask>>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectTaskById(taskId: string): Promise<ProjectTaskDetail> {
    const url = `/ProjectTask/${taskId}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ProjectTaskDetail> => {
      const response = await apiClient.get<ProjectTaskDetail>(url, { signal });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async getProjectTaskSubtasks(
    taskId: string,
    params?: {
      maxSize?: number;
      offset?: number;
      orderBy?: string;
      order?: "asc" | "desc";
      primaryFilter?: string;
      attributeSelect?: string;
    },
  ): Promise<ListResponse<ProjectTask>> {
    const {
      maxSize = 15,
      offset = 0,
      orderBy = "order",
      order = "asc",
      primaryFilter = "",
      attributeSelect = "timeline,assignedUserId,assignedUserName,columnId,columnName,name",
    } = params || {};

    const queryParams = new URLSearchParams({
      maxSize: maxSize.toString(),
      offset: offset.toString(),
      orderBy,
      order,
      attributeSelect,
    });

    if (primaryFilter) {
      queryParams.append("primaryFilter", primaryFilter);
    }

    const url = `/ProjectTask/${taskId}/subTasks?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<ProjectTask>> => {
      const response = await apiClient.get<ListResponse<ProjectTask>>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }

  async createProjectGroup(data: {
    projectId: string;
    projectName: string;
    name: string;
    color?: number;
    taskPlacement?: string;
    type?: string;
  }): Promise<ProjectGroup> {
    const url = `/ProjectGroup`;
    const response = await apiClient.post<ProjectGroup>(url, {
      projectId: data.projectId,
      projectName: data.projectName,
      name: data.name,
      color: data.color ?? 0,
      taskPlacement: data.taskPlacement || "Bottom",
      type: data.type || "Work",
    });

    await this.invalidateProjectPlanCache(data.projectId);

    return response.data;
  }

  async updateProjectGroup(
    groupId: string,
    data: {
      name?: string;
      color?: number;
      taskPlacement?: string;
      type?: string;
    },
  ): Promise<ProjectGroup> {
    const url = `/ProjectGroup/${groupId}`;
    const response = await apiClient.put<ProjectGroup>(url, data);

    const projectId = response.data.projectId;
    if (projectId) {
      await this.invalidateProjectPlanCache(projectId);
    }

    return response.data;
  }

  async deleteProjectGroup(groupId: string, projectId: string): Promise<void> {
    const url = `/ProjectGroup/${groupId}`;
    await apiClient.delete(url);

    await this.invalidateProjectPlanCache(projectId);
  }

  private async invalidateProjectPlanCache(projectId: string): Promise<void> {
    const { removeFromCache, getCacheKeys } =
      await import("../storage/cacheManager");
    const allKeys = await getCacheKeys();
    const planCacheKeys = allKeys.filter(
      (key) =>
        key.includes(`/Project/${projectId}/plan`) ||
        key.includes(`Project/${projectId}/plan`),
    );
    await Promise.all(planCacheKeys.map((key) => removeFromCache(key)));
  }

  private async invalidateProjectTaskCache(
    taskId?: string,
    projectId?: string,
    groupId?: string,
  ): Promise<void> {
    const { removeFromCache, getCacheKeys } =
      await import("../storage/cacheManager");
    const allKeys = await getCacheKeys();

    const keysToRemove = allKeys.filter((key) => {
      if (taskId && key.startsWith(`/ProjectTask/${taskId}`)) {
        return true;
      }
      if (
        projectId &&
        (key.includes(`/Project/${projectId}/tasks`) ||
          key.includes(`Project/${projectId}/tasks`))
      ) {
        return true;
      }
      if (
        groupId &&
        (key.includes(`/ProjectGroup/${groupId}/tasks`) ||
          key.includes(`ProjectGroup/${groupId}/tasks`))
      ) {
        return true;
      }
      return false;
    });

    await Promise.all(keysToRemove.map((key) => removeFromCache(key)));
  }

  async createProjectTask(
    data: Partial<ProjectTaskDetail>,
  ): Promise<ProjectTaskDetail> {
    const url = `/ProjectTask`;
    const response = await apiClient.post<ProjectTaskDetail>(url, data);

    if (data.projectId) {
      await this.invalidateProjectPlanCache(data.projectId);
    }
    await this.invalidateProjectTaskCache(
      response.data.id,
      data.projectId,
      (data as { groupId?: string }).groupId,
    );

    return response.data;
  }

  async updateProjectTask(
    taskId: string,
    data: Partial<ProjectTaskDetail>,
  ): Promise<ProjectTaskDetail> {
    const url = `/ProjectTask/${taskId}`;
    const response = await apiClient.put<ProjectTaskDetail>(url, data);

    const projectId = data.projectId || response.data.projectId;
    if (projectId) {
      await this.invalidateProjectPlanCache(projectId);
    }
    await this.invalidateProjectTaskCache(
      taskId,
      projectId,
      (data as { groupId?: string }).groupId,
    );

    return response.data;
  }
}

class ProjectBoardService {
  async getBoards(): Promise<ListResponse<ProjectBoard>> {
    const queryParams = new URLSearchParams({
      maxSize: "50",
      select: "id,name",
      primaryFilter: "actual",
      q: "",
    });
    const url = `/ProjectBoard?${queryParams.toString()}`;
    const cacheKey = generateCacheKey(url, {});
    const requestKey = generateRequestKey("GET", url);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<ProjectBoard>> => {
      const response = await apiClient.get<ListResponse<ProjectBoard>>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, 5 * 60 * 1000);
  }
}

class AccountService extends BaseService<Account> {
  constructor() {
    super("Account");
    this.setCacheStrategy("network-first");
    this.setCacheExpiry(5 * 60 * 1000);
  }

  async getAccounts(params?: {
    maxSize?: number;
    offset?: number;
    orderBy?: string;
    order?: "asc" | "desc";
  }): Promise<ListResponse<Account>> {
    const {
      maxSize = 50,
      offset = 0,
      orderBy = "createdAt",
      order = "desc",
    } = params || {};

    const queryParams = new URLSearchParams({
      maxSize: maxSize.toString(),
      offset: offset.toString(),
      orderBy,
      order,
      attributeSelect: "billingAddressCountry,name",
    });
    const url = `/Account?${queryParams.toString()}`;
    const cacheKey = generateCacheKey("/Account/list", {
      maxSize,
      offset,
      orderBy,
      order,
    });
    const requestKey = generateRequestKey("GET", url, params);

    const fetchData = async (
      signal?: AbortSignal,
    ): Promise<ListResponse<Account>> => {
      const response = await apiClient.get<ListResponse<Account>>(url, {
        signal,
      });
      return response.data;
    };

    const deduplicatedFetch = () =>
      requestDeduplication.execute(requestKey, fetchData);

    return staleWhileRevalidate(cacheKey, deduplicatedFetch, this.cacheExpiry);
  }
}

export const projectService = new ProjectService();
export const projectBoardService = new ProjectBoardService();
export const accountService = new AccountService();

export type {
  Project,
  ProjectListResponse,
  ProjectMember,
  ProjectTask,
  ProjectStreamItem,
  ProjectBoard,
  Account,
};
export const getProjects = (params?: {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: "asc" | "desc";
  attributeSelect?: string[];
}) => projectService.getProjects(params);
export const getProjectById = (projectId: string) =>
  projectService.getProjectById(projectId);
export const createProject = (data: Partial<Project>) =>
  projectService.createProject(data);
export const updateProject = (projectId: string, data: Partial<Project>) =>
  projectService.update(projectId, data);
export const getProjectMembers = (projectId: string) =>
  projectService.getProjectMembers(projectId);
export const getProjectTasks = (projectId: string) =>
  projectService.getProjectTasks(projectId);
export const getProjectStream = (projectId: string) =>
  projectService.getProjectStream(projectId);
export const getProjectAttachments = (projectId: string) =>
  projectService.getProjectAttachments(projectId);
export const getProjectBoards = () => projectBoardService.getBoards();
export const getAccounts = (params?: {
  maxSize?: number;
  offset?: number;
  orderBy?: string;
  order?: "asc" | "desc";
}) => accountService.getAccounts(params);
export const getProjectPlan = (
  projectId: string,
  params?: {
    maxSize?: number;
    select?: string;
    primaryFilter?: string;
    collapsedGroupIds?: string;
  },
) => projectService.getProjectPlan(projectId, params);
export const getGroupTasks = (
  groupId: string,
  params?: {
    maxSize?: number;
    offset?: number;
    orderBy?: string;
    order?: "asc" | "desc";
    primaryFilter?: string;
    attributeSelect?: string;
  },
) => projectService.getGroupTasks(groupId, params);
export const getProjectTaskById = (taskId: string) =>
  projectService.getProjectTaskById(taskId);
export const getProjectTaskSubtasks = (
  taskId: string,
  params?: {
    maxSize?: number;
    offset?: number;
    orderBy?: string;
    order?: "asc" | "desc";
    primaryFilter?: string;
    attributeSelect?: string;
  },
) => projectService.getProjectTaskSubtasks(taskId, params);
export const createProjectGroup = (data: {
  projectId: string;
  projectName: string;
  name: string;
  color?: number;
  taskPlacement?: string;
  type?: string;
}) => projectService.createProjectGroup(data);
export const updateProjectGroup = (
  groupId: string,
  data: {
    name?: string;
    color?: number;
    taskPlacement?: string;
    type?: string;
  },
) => projectService.updateProjectGroup(groupId, data);
export const deleteProjectGroup = (groupId: string, projectId: string) =>
  projectService.deleteProjectGroup(groupId, projectId);
export const createProjectTask = (data: Partial<ProjectTaskDetail>) =>
  projectService.createProjectTask(data);
export const updateProjectTask = (
  taskId: string,
  data: Partial<ProjectTaskDetail>,
) => projectService.updateProjectTask(taskId, data);
